import { type BoneMetadata } from './BoneAutoMapper'

/**
 * UnityHumanoidMapper
 *
 * Maps the app's internal humanoid bone names (close to the Unreal
 * SK_Mannequin convention used by `DEFAULT_HUMANOID_DRIVE_SPEC`) to Unity's
 * Humanoid avatar bone names. Use when exporting an animation that should
 * land on a Unity Humanoid rig — the Animator drives bones by these exact
 * names.
 *
 * Reference: Unity Manual → Configuring the Avatar →
 * https://docs.unity3d.com/Manual/AvatarCreationandSetup.html
 */
export class UnityHumanoidMapper {
  /** Internal bone name → Unity Humanoid bone name. */
  private static readonly BONE_MAP: Record<string, string> = {
    // Torso & head
    pelvis: 'Hips',
    spine_01: 'Spine',
    spine_02: 'Chest',
    spine_03: 'UpperChest',
    neck_01: 'Neck',
    head: 'Head',

    // Left arm
    clavicle_l: 'LeftShoulder',
    upperarm_l: 'LeftUpperArm',
    lowerarm_l: 'LeftLowerArm',
    hand_l: 'LeftHand',

    // Right arm
    clavicle_r: 'RightShoulder',
    upperarm_r: 'RightUpperArm',
    lowerarm_r: 'RightLowerArm',
    hand_r: 'RightHand',

    // Left leg
    thigh_l: 'LeftUpperLeg',
    calf_l: 'LeftLowerLeg',
    foot_l: 'LeftFoot',
    ball_l: 'LeftToes',

    // Right leg
    thigh_r: 'RightUpperLeg',
    calf_r: 'RightLowerLeg',
    foot_r: 'RightFoot',
    ball_r: 'RightToes',

    // Fingers — Unity Humanoid uses Proximal/Intermediate/Distal naming.
    // Left thumb only has Proximal/Intermediate/Distal (no metacarpal in
    // Unity's standard humanoid spec).
    thumb_01_l: 'LeftThumbProximal',
    thumb_02_l: 'LeftThumbIntermediate',
    thumb_03_l: 'LeftThumbDistal',
    index_01_l: 'LeftIndexProximal',
    index_02_l: 'LeftIndexIntermediate',
    index_03_l: 'LeftIndexDistal',
    middle_01_l: 'LeftMiddleProximal',
    middle_02_l: 'LeftMiddleIntermediate',
    middle_03_l: 'LeftMiddleDistal',
    ring_01_l: 'LeftRingProximal',
    ring_02_l: 'LeftRingIntermediate',
    ring_03_l: 'LeftRingDistal',
    pinky_01_l: 'LeftLittleProximal',
    pinky_02_l: 'LeftLittleIntermediate',
    pinky_03_l: 'LeftLittleDistal',

    thumb_01_r: 'RightThumbProximal',
    thumb_02_r: 'RightThumbIntermediate',
    thumb_03_r: 'RightThumbDistal',
    index_01_r: 'RightIndexProximal',
    index_02_r: 'RightIndexIntermediate',
    index_03_r: 'RightIndexDistal',
    middle_01_r: 'RightMiddleProximal',
    middle_02_r: 'RightMiddleIntermediate',
    middle_03_r: 'RightMiddleDistal',
    ring_01_r: 'RightRingProximal',
    ring_02_r: 'RightRingIntermediate',
    ring_03_r: 'RightRingDistal',
    pinky_01_r: 'RightLittleProximal',
    pinky_02_r: 'RightLittleIntermediate',
    pinky_03_r: 'RightLittleDistal'
  }

  static map_source_bone_name (source_bone_name: string): string | undefined {
    return this.BONE_MAP[source_bone_name]
  }

  /**
   * Reverse map for runtime track renaming when importing a Unity-authored
   * animation file. Mirrors MixamoMapper.get_source_to_target_map.
   */
  static get_target_to_source_map (): Record<string, string> {
    const map: Record<string, string> = {}
    for (const [src, dst] of Object.entries(this.BONE_MAP)) {
      map[dst] = src
    }
    return map
  }

  /**
   * Map source (internal) bones to Unity Humanoid target bones. Returns
   * `Map<target_bone_name, source_bone_name>` — same shape as MixamoMapper.
   */
  static map_bones (source_bones: BoneMetadata[], target_bones: BoneMetadata[]): Map<string, string> {
    const mappings = new Map<string, string>()
    for (const source_bone of source_bones) {
      const expected = this.BONE_MAP[source_bone.name]
      if (expected === undefined) continue
      const target = target_bones.find(tb => tb.name === expected)
      if (target !== undefined) mappings.set(target.name, source_bone.name)
    }
    return mappings
  }
}
